import { callWaiter } from 'redux-waiter'
import { actions as notificationActions } from 'modules/notification-manager'
import { getLocationsBatch, updatePersonRatingArea, findMatchingLocation } from './services'
import actionTypes from './actionTypes'
import { PersonWithAddress, RatingAreaRecalculationResult, RatingAreaUpdateResult } from './types'

/**
 * Recalculate rating areas for all family members
 * This is the main action that orchestrates the entire process
 * @param familyMembers - Array of family members with address info
 * @param enrollmentYear - The enrollment benefit year
 * @returns Redux thunk action
 */
export const recalculateRatingAreas = (familyMembers: PersonWithAddress[], enrollmentYear: number = new Date().getFullYear() + 1) => (dispatch: any) => {
  return new Promise<RatingAreaRecalculationResult>((resolve, reject) => {
    dispatch({
      type: actionTypes.RECALCULATE_RATING_AREAS,
      payload: { familyMembers, enrollmentYear },
    })

    try {
      // Filter out persons without rating areas or postal codes
      const eligiblePersons = familyMembers.filter(
        (person) => person.RatingArea && person.PostalCode
      )

      if (eligiblePersons.length === 0) {
        console.log('No eligible persons found for rating area recalculation')
        const result: RatingAreaRecalculationResult = {
          totalPersons: familyMembers.length,
          updatedPersons: 0,
          results: [],
        }
        dispatch({
          type: actionTypes.RECALCULATE_RATING_AREAS_SUCCESS,
          payload: result,
        })
        resolve(result)
        return
      }

      // Get unique zip codes for batch lookup
      const zipCodes = eligiblePersons.map((person) => person.PostalCode)
      
      // Make batch call to get locations
      dispatch(getLocationsBatch(zipCodes, enrollmentYear))
        .then(async (locations: any) => {
          console.log(`Retrieved ${locations.length} locations for ${zipCodes.length} zip codes`)
          
          // Process each person sequentially to avoid race conditions
          const results: RatingAreaUpdateResult[] = []
          
          for (const person of eligiblePersons) {
            const matchingLocation = findMatchingLocation(person, locations)
            
            if (!matchingLocation) {
              console.warn(`No exact location match found for personId=${person.Id}`)
              results.push({
                personId: person.Id,
                personName: `${person.FirstName} ${person.LastName}`,
                oldRatingArea: person.RatingArea,
                newRatingArea: person.RatingArea,
                updated: false,
                error: 'No exact location match found',
              })
              continue
            }

            // Check if rating area has changed
            if (matchingLocation.ratingArea === person.RatingArea) {
              console.log(`Rating area unchanged for personId=${person.Id}`)
              results.push({
                personId: person.Id,
                personName: `${person.FirstName} ${person.LastName}`,
                oldRatingArea: person.RatingArea,
                newRatingArea: person.RatingArea,
                updated: false, 
              })
              continue
            }

            // Update the person's rating area
            try {
              await dispatch(updatePersonRatingArea(person, matchingLocation.ratingArea))
              results.push({
                personId: person.Id,
                personName: `${person.FirstName} ${person.LastName}`,
                oldRatingArea: person.RatingArea,
                newRatingArea: matchingLocation.ratingArea,
                updated: true,
              })
            } catch (error: any) {
              console.error(`Failed to update rating area for personId=${person.Id}:`, error)
              results.push({
                personId: person.Id,
                personName: `${person.FirstName} ${person.LastName}`,
                oldRatingArea: person.RatingArea,
                newRatingArea: person.RatingArea,
                updated: false,
                error: error.message,
              })
            }
          }

          // Process results
          const updatedCount = results.filter((result) => result.updated).length
          
          console.log(`Rating area recalculation completed: ${updatedCount} of ${eligiblePersons.length} persons updated`)
          
          const finalResult: RatingAreaRecalculationResult = {
            totalPersons: familyMembers.length,
            updatedPersons: updatedCount,
            results,
          }

          dispatch({
            type: actionTypes.RECALCULATE_RATING_AREAS_SUCCESS,
            payload: finalResult,
          })

          // Show success notification if any rating areas were updated
          if (updatedCount > 0) {
            dispatch(
              notificationActions.createNotification({
                type: 'success',
                autoClose: true,
                message: `Family rating areas updated for ${enrollmentYear}`,
              })
            )
          } else {
            console.log('No rating areas were updated during recalculation')
          }

          resolve(finalResult)
        })
        .catch((error: any) => {
          console.error('Error getting batch locations:', error)
          dispatch({
            type: actionTypes.RECALCULATE_RATING_AREAS_ERROR,
            payload: error.message,
          })
          reject(error)
        })
    } catch (error: any) {
      console.error('Error in rating area recalculation:', error)
      dispatch({
        type: actionTypes.RECALCULATE_RATING_AREAS_ERROR,
        payload: error.message,
      })
      reject(error)
    }
  })
}
